/**
 * Feedback de "item adicionado" para quando o carrinho deslizante NÃO abre (mobile, < 750px).
 * Chamado por `CartDrawer.renderContents` (cart-drawer.js). Quatro sinais, todos opcionais:
 *   1. a imagem do produto "voa" até o ícone do carrinho
 *   2. o ícone dá um pulo ao receber o item
 *   3. o botão clicado vira "✓ Adicionado" por ~1,6s
 *   4. vibração curta + anúncio para leitor de tela
 * Com `prefers-reduced-motion` ficam só o texto do botão e o anúncio (sem voo, pulo ou vibração).
 */
(() => {
  const BUTTON_FEEDBACK_MS = 1600;
  const FLY_MS = 650;
  const TRIGGER_MAX_AGE_MS = 10000;

  let lastTrigger = null;
  let lastTriggerAt = 0;

  // O botão de enviar nem sempre é o document.activeElement (Safari iOS não foca <button> no toque),
  // então guardamos o último botão de submit clicado. `click` também cobre Enter no teclado.
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.('button[type="submit"]');
      if (!button) return;
      lastTrigger = button;
      lastTriggerAt = Date.now();
    },
    true
  );

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const isInViewport = (rect) =>
    rect.width > 0 &&
    rect.height > 0 &&
    rect.bottom > 0 &&
    rect.right > 0 &&
    rect.top < window.innerHeight &&
    rect.left < window.innerWidth;

  /** Imagem de origem do voo: a do produto que contém o botão (card, página de produto ou quick-add). */
  const findProductImage = (trigger) => {
    const container = trigger?.closest('product-info, .card-wrapper, quick-add-modal, .product');
    const images = [...(container?.querySelectorAll('img') ?? [])].filter((img) => img.currentSrc || img.src);
    return images.find((img) => isInViewport(img.getBoundingClientRect())) ?? images[0] ?? null;
  };

  const showButtonLabel = (trigger, label) => {
    if (!trigger?.isConnected || trigger.classList.contains('cart-added')) return;

    // Sobreposição absoluta: os filhos do botão ficam intactos (o product-form guarda referência a eles).
    const overlay = document.createElement('span');
    overlay.className = 'cart-added__overlay';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.innerHTML =
      '<svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M4 10.5l4 4 8-9"/></svg>';
    overlay.append(document.createTextNode(label));

    trigger.classList.add('cart-added');
    trigger.append(overlay);

    setTimeout(() => {
      overlay.remove();
      trigger.classList.remove('cart-added');
    }, BUTTON_FEEDBACK_MS);
  };

  const bounceCartIcon = (icon) => {
    if (!icon) return;
    icon.classList.remove('cart-icon-added');
    // reflow para reiniciar a animação quando o cliente adiciona de novo em sequência
    void icon.offsetWidth;
    icon.classList.add('cart-icon-added');
    icon.addEventListener('animationend', () => icon.classList.remove('cart-icon-added'), { once: true });
  };

  const flyToCart = (trigger, icon) => {
    const iconRect = icon?.getBoundingClientRect();
    if (!iconRect || !isInViewport(iconRect)) return Promise.resolve();

    const image = findProductImage(trigger);
    if (!image) return Promise.resolve();

    const imageRect = image.getBoundingClientRect();
    const triggerRect = trigger?.getBoundingClientRect();

    // Imagem fora da tela (ex.: barra fixa depois de rolar a página): o voo parte do botão.
    let start = imageRect;
    if (!isInViewport(imageRect)) {
      if (!triggerRect || !isInViewport(triggerRect)) return Promise.resolve();
      const size = 64;
      start = {
        left: triggerRect.left + triggerRect.width / 2 - size / 2,
        top: triggerRect.top + triggerRect.height / 2 - size / 2,
        width: size,
        height: size,
      };
    }

    // Cap no tamanho: a imagem da página de produto ocupa a tela inteira.
    const size = Math.min(start.width, start.height, 160);
    const startLeft = start.left + (start.width - size) / 2;
    const startTop = start.top + (start.height - size) / 2;

    const clone = document.createElement('img');
    clone.src = image.currentSrc || image.src;
    clone.alt = '';
    clone.setAttribute('aria-hidden', 'true');
    clone.className = 'cart-fly-image';
    clone.style.cssText = `left:${startLeft}px;top:${startTop}px;width:${size}px;height:${size}px;`;
    document.body.append(clone);

    const dx = iconRect.left + iconRect.width / 2 - (startLeft + size / 2);
    const dy = iconRect.top + iconRect.height / 2 - (startTop + size / 2);

    const animation = clone.animate(
      [
        { transform: 'translate(0, 0) scale(1)', opacity: 0.95 },
        { transform: `translate(${dx}px, ${dy}px) scale(${24 / size})`, opacity: 0.35 },
      ],
      { duration: FLY_MS, easing: 'cubic-bezier(0.55, 0, 0.75, 0.35)' }
    );

    return animation.finished.catch(() => {}).finally(() => clone.remove());
  };

  const announce = (message) => {
    const status = document.getElementById('CartDrawer-LiveRegionText');
    if (!status || !message) return;
    status.textContent = message;
    status.setAttribute('aria-hidden', 'false');
    setTimeout(() => status.setAttribute('aria-hidden', 'true'), 3000);
  };

  window.CartAddFeedback = {
    /**
     * @param {Element | null} fallbackTrigger - elemento ativo no envio, usado se não houver clique recente.
     * @param {{ label?: string, message?: string }} [text] - textos já traduzidos (data-attributes do <cart-drawer>).
     */
    play(fallbackTrigger, { label = '', message = '' } = {}) {
      const recent = lastTrigger?.isConnected && Date.now() - lastTriggerAt < TRIGGER_MAX_AGE_MS;
      const trigger = recent ? lastTrigger : fallbackTrigger;
      const icon = document.getElementById('cart-icon-bubble');
      const reduced = prefersReducedMotion();

      if (label) showButtonLabel(trigger, label);
      announce(message);

      if (reduced) return;

      navigator.vibrate?.(12);
      flyToCart(trigger, icon).then(() => bounceCartIcon(icon));
    },
  };
})();
